import {
  AddressAutocomplete,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  CompanySearchInput,
  DateTimeInput,
  Form,
  HiscoxActivityFormField,
  HiscoxMrpExcessFormField,
  FormField,
  FormLabel,
  Input,
  Label,
  MultiSelect,
  PhoneNumberInput,
  RadioGroup,
  RadioGroupItem,
  SearchableSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextareaWithMic,
  VehicleBrandFormField,
  VehicleInputFormField,
  VehicleModelFormField,
  VehicleRegistrationNumberInput,
  VehicleVersionFormField,
} from '@folio/components';
import { AddressSearchSessionProvider } from '../../features/address-autocomplete/address-search-session';
import { SelectCards } from '../select-cards/SelectCards';
import { NumberQuickOptions } from '../number-quick-options/NumberQuickOptions';
import { RadioToggle } from '../radio-toggle/RadioToggle';
import { cn, getCountryOptions } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';
import type {
  ConditionalVisibility,
  Field,
  Fields,
  FormSection,
  FormSections,
} from 'korint-sdk/react';
import _ from 'lodash';
import * as React from 'react';
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
  type UseFormReturn,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { FormBuilderField } from './FormBuilderField';
import { evaluateCondition } from './conditionalVisibilityUtils';
import { parseDateLimit } from '@folio/utils/date-limits';
import { useContractTimeZone } from '@folio/utils/time-zone';
import { FormMonthInput } from '@folio/components/features/FormMonthInput';
import { useTranslation } from 'react-i18next';
import { autoFillFromPlaceholders } from '@folio/utils/autoFillFromPlaceholders';
import { shouldShowAutoFill } from '@folio/utils';
import { Wand2 } from 'lucide-react';
import { ComputedFieldsFormErrors } from '../ComputedFieldsFormErrors';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components/ui/table';
import { Plus, Trash2 } from 'lucide-react';
import { FormControl, FormItem, FormMessage } from '../form';
import { RequiredAsterisk } from '../RequiredAsterisk/RequiredAsterisk';

export type FormBuilderProps<TFormValues extends FieldValues> = {
  fields: Fields;
  form: UseFormReturn<TFormValues>;
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  externalData?: Record<string, unknown>;
  portalTarget?: HTMLElement | null;
};

export type SectionedFormBuilderProps<TFormValues extends FieldValues> = Omit<
  FormBuilderProps<TFormValues>,
  'fields'
> & {
  sections: FormSections;
  disabled?: boolean;
};

type FormBuilderInputRendererProps<TFormValues extends FieldValues> = {
  fieldDefinition: Field;
  rhfField: ControllerRenderProps<TFormValues, Path<TFormValues>>;
  portalTarget?: HTMLElement | null;
  externalData?: Record<string, unknown>;
};

function resolveDateTimeToken(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const parsed = parseDateLimit(value);
  // An unparseable bound falls through to the browser input, as before.
  return parsed?.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : value;
}

/**
 * Native <input type="date"> requires a YYYY-MM-DD value. Form values can arrive
 * from AI extraction in other shapes (DD/MM/YYYY, ISO 8601 with time, Date
 * `toString()` output, etc.) — this coerces to ISO day-precision, or returns
 * empty when the input is unparseable so the field stays blank rather than
 * crashing the browser's date picker.
 */
function toIsoDate(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value !== 'string') {
    const direct = dayjs(value as string | number | Date);
    return direct.isValid() ? direct.format('YYYY-MM-DD') : '';
  }

  // Already ISO-shaped (YYYY-MM-DD, optionally with time / timezone). Return
  // the date prefix directly — round-tripping through dayjs would remap years
  // 0–99 to 1900–1999 (legacy `new Date(year, …)` quirk), and the browser
  // emits transient values like "0001-01-01" while the user is typing the
  // year segment. Rewriting those mid-keystroke resets the segment cursor.
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // European format: DD/MM/YYYY or DD-MM-YYYY. Construct ISO directly to
  // avoid the same dayjs year-0–99 remap.
  const euroMatch = value.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (euroMatch) {
    const [, d, m, y] = euroMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  const fallback = dayjs(value);
  return fallback.isValid() ? fallback.format('YYYY-MM-DD') : '';
}

function useFirmTypeVisibility(firmTypes?: Field['firmTypes']): boolean {
  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    enabled: !!firmTypes,
  });
  const brokerageFirmId = user?.isSuperUser ? undefined : user?.brokerageFirmId;
  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!firmTypes && !!brokerageFirmId,
  });

  if (!firmTypes || user?.isSuperUser) {
    return true;
  }

  return !!brokerageFirm?.firmType && firmTypes.includes(brokerageFirm.firmType);
}

/**
 * Hook to evaluate conditional visibility for multiple conditions
 */
function useConditionalVisibility(
  conditions?: Array<ConditionalVisibility>,
  externalData?: Record<string, unknown>,
): boolean {
  const form = useFormContext<FieldValues>();

  // Watch all field names that are referenced in conditions
  const fieldNames = conditions?.filter(c => c.fieldName).map(c => c.fieldName!) ?? [];
  const watchedValues = useWatch({
    control: form.control,
    name: fieldNames.length > 0 ? (fieldNames as Array<Path<FieldValues>>) : [],
    disabled: fieldNames.length === 0,
  });

  if (!conditions || conditions.length === 0) {
    return true;
  }

  // Evaluate each condition
  return conditions.every(condition => {
    let sourceValue: unknown;

    if (condition.fieldName) {
      // Get value from form data
      const fieldIndex = fieldNames.indexOf(condition.fieldName);
      sourceValue = Array.isArray(watchedValues) ? watchedValues[fieldIndex] : watchedValues;
    } else if (condition.apiProperty && externalData) {
      // Get value from external data
      sourceValue = _.get(externalData, condition.apiProperty);
    } else {
      // No valid data source specified, default to visible
      return true;
    }

    return evaluateCondition(condition, sourceValue);
  });
}

/**
 * Wrapper component that handles conditional field visibility
 */
const LG_COL_SPAN_CLASSES: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
};

function ConditionalFieldWrapper({
  field,
  children,
  externalData,
  className,
}: {
  field: Field;
  children: React.ReactNode;
  externalData?: Record<string, unknown>;
  className?: string;
}) {
  const isVisible = useConditionalVisibility(field.showWhen, externalData);
  const isVisibleToFirm = useFirmTypeVisibility(field.firmTypes);

  if (!isVisible || !isVisibleToFirm) {
    return null;
  }

  if (className) {
    return <div className={className}>{children}</div>;
  }

  return <>{children}</>;
}

/**
 * Wrapper component that handles conditional section visibility
 */
function ConditionalSectionWrapper({
  section,
  children,
  externalData,
}: {
  section: FormSection;
  children: React.ReactNode;
  externalData?: Record<string, unknown>;
}) {
  const isVisible = useConditionalVisibility(section.showWhen, externalData);
  const form = useFormContext<FieldValues>();
  const prevVisibleRef = React.useRef(isVisible);

  React.useEffect(() => {
    if (prevVisibleRef.current && !isVisible && section.clearOnHide) {
      for (const field of section.fields) {
        const fieldName = typeof field === 'string' ? field : field.name;
        if (fieldName) {
          form.setValue(fieldName as Path<FieldValues>, undefined);
        }
      }
    }
    prevVisibleRef.current = isVisible;
  }, [isVisible, section, form]);

  if (!isVisible) {
    return null;
  }

  return <>{children}</>;
}

// Number fields flagged `autoComputed` are derived live from sibling fields and
// rendered read-only. Returns the computed value (undefined for any other field),
// and keeps the RHF value in sync so it is submitted with the form.
function useAutoCalculatedValue<TFormValues extends FieldValues>(
  fieldDefinition: Field,
  rhfField: ControllerRenderProps<TFormValues, Path<TFormValues>>,
): number | undefined {
  const autoComputed = fieldDefinition.type === 'number' ? fieldDefinition.autoComputed : undefined;

  const { name } = fieldDefinition;
  const lastDot = name.lastIndexOf('.');
  const scope = lastDot >= 0 ? name.slice(0, lastDot + 1) : '';
  const scopedDeps = (autoComputed?.dependsOn ?? []).map(dep => `${scope}${dep}`);

  const depValues = useWatch({ name: scopedDeps }) as unknown[];
  const autoCalculatedValue = autoComputed?.compute(
    (depValues ?? []).map(value => (value === '' || value == null ? 0 : Number(value))),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: guard prevents the onChange loop
  React.useEffect(() => {
    if (autoCalculatedValue !== undefined && rhfField.value !== autoCalculatedValue) {
      rhfField.onChange(autoCalculatedValue);
    }
  }, [autoCalculatedValue]);

  return autoCalculatedValue;
}

function ArrayFormField({
  fieldDefinition,
  portalTarget,
  externalData,
}: {
  fieldDefinition: Extract<Field, { type: 'array' }>;
  portalTarget?: HTMLElement | null;
  externalData?: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const form = useFormContext();
  const { name, columns, maxItems, disabled } = fieldDefinition;
  const { fields: rows, append, remove } = useFieldArray({ control: form.control, name });
  const actualFieldName =
    name.startsWith('assets.') || name.startsWith('customers.')
      ? name.split('.').slice(2).join('.')
      : name;
  const canAddRow = !disabled && (maxItems === undefined || rows.length < maxItems);

  return (
    <Stack className="gap-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead />
            {columns.map(column => (
              <TableHead key={column.name}>
                <span className="flex">
                  {column.label ?? t(`fields.${actualFieldName}.columns.${column.name}`)}
                  {column.required && <RequiredAsterisk />}
                </span>
              </TableHead>
            ))}
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id}>
              <TableCell className="align-top whitespace-nowrap pt-5 font-medium">
                {t([`fields.${actualFieldName}.rowLabel`, 'arrayField.rowLabel'], {
                  index: index + 1,
                })}
              </TableCell>
              {columns.map(column => {
                const cellName = `${name}.${index}.${column.name}`;
                return (
                  <TableCell key={column.name} className="align-top">
                    <FormField
                      control={form.control}
                      name={cellName}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FormBuilderInputRenderer
                              fieldDefinition={{ ...column, name: cellName, label: '', disabled }}
                              rhfField={field}
                              portalTarget={portalTarget}
                              externalData={externalData}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                );
              })}
              <TableCell className="align-top">
                {!disabled && (
                  <Button
                    type="button"
                    variant="destructive-ghost"
                    size="icon"
                    aria-label={t('arrayField.removeRow')}
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canAddRow && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => append({})}
        >
          <Plus className="h-4 w-4" />
          {t('arrayField.addRow')}
        </Button>
      )}
    </Stack>
  );
}

function FormBuilderInputRenderer<TFormValues extends FieldValues>({
  fieldDefinition,
  rhfField,
  portalTarget,
  externalData,
}: FormBuilderInputRendererProps<TFormValues>) {
  const { t, i18n } = useTranslation();
  const autoCalculatedValue = useAutoCalculatedValue(fieldDefinition, rhfField);
  const contractTimeZone = useContractTimeZone();

  // AssetFunnelModule and CustomerFunnelModule format field names like so: {arrayName}.${index}.${actualFieldName}
  const actualFieldName =
    fieldDefinition.name.startsWith('assets.') || fieldDefinition.name.startsWith('customers.')
      ? fieldDefinition.name.split('.').slice(2).join('.')
      : fieldDefinition.name;

  /**
   * @debt fieldDefinition placeholder is deprecated and should be removed once everything wording config was moved to i18n
   */
  const placeholder =
    fieldDefinition.DEPRECATED_placeholder ??
    t([
      `fields.${actualFieldName}.placeholder`,
      `fields.${fieldDefinition.type}.placeholder`,
      'empty',
    ]);

  if (fieldDefinition.addressPart) {
    // A country is a closed set — picked locally, never asked of the address provider.
    if (fieldDefinition.addressPart === 'country') {
      return (
        <SearchableSelect
          name={fieldDefinition.name}
          options={getCountryOptions(i18n.language)}
          value={rhfField.value || ''}
          onChange={rhfField.onChange}
          clearable={!fieldDefinition.required}
          searchable
          placeholder={placeholder}
        />
      );
    }
    const FieldIcon = fieldDefinition.icon;
    return (
      <AddressAutocomplete
        name={rhfField.name}
        label={
          fieldDefinition.label ??
          t([`fields.${actualFieldName}.form.label`, `fields.${actualFieldName}.label`])
        }
        part={fieldDefinition.addressPart}
        value={rhfField.value || ''}
        onChange={rhfField.onChange}
        onBlur={rhfField.onBlur}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
        addressGroupKey={fieldDefinition.addressGroupKey}
        siblingFields={fieldDefinition.siblingFields}
        countries={'countries' in fieldDefinition ? fieldDefinition.countries : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'text') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        value={rhfField.value || ''}
        onChange={
          fieldDefinition.uppercase
            ? e => rhfField.onChange(e.target.value.toUpperCase())
            : rhfField.onChange
        }
        className={fieldDefinition.uppercase ? 'uppercase' : undefined}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'textarea') {
    // Mic is enabled when the field opts in (`speechToText: true` in the field
    // definition) AND the tenant config has an speechToText provider (resolved inside
    // TextareaWithMic).
    const fieldSpeechToTextEnabled = fieldDefinition.speechToText === true;
    return (
      <TextareaWithMic
        ref={rhfField.ref}
        name={rhfField.name}
        onBlur={rhfField.onBlur}
        value={rhfField.value || ''}
        onValueChange={rhfField.onChange}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        rows={fieldDefinition.rows}
        cols={fieldDefinition.cols}
        speechToText={fieldSpeechToTextEnabled ? {} : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'readonly') {
    return (
      <div className="text-sm">
        {rhfField.value !== undefined && rhfField.value !== null ? String(rhfField.value) : '-'}
      </div>
    );
  }

  if (fieldDefinition.type === 'phoneNumber') {
    return (
      <PhoneNumberInput
        name={rhfField.name}
        ref={rhfField.ref}
        value={rhfField.value}
        onChange={rhfField.onChange}
        onBlur={rhfField.onBlur}
        countries={fieldDefinition.countries}
        required={fieldDefinition.required}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
      />
    );
  }

  if (fieldDefinition.type === 'postcode') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        value={rhfField.value || ''}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'address') {
    const FieldIcon = fieldDefinition.icon;
    // Autocomplete (when flagged + enabled) is handled by the anchor branch above;
    // here we render only the plain fallback input (flag off, or no autocomplete flag).
    return (
      <Input
        {...rhfField}
        value={rhfField.value || ''}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'city') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        value={rhfField.value || ''}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'country') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        value={rhfField.value || ''}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'email') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        value={rhfField.value || ''}
        placeholder={placeholder}
        type="email"
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'number') {
    const isAutoCalculated = autoCalculatedValue !== undefined;

    if (!isAutoCalculated && fieldDefinition.display === 'cards') {
      {
        const resolvedDisplayMax = fieldDefinition.displayMax ?? fieldDefinition.max ?? 10;
        const resolvedMax = fieldDefinition.max;
        return (
          <NumberQuickOptions
            name={fieldDefinition.name}
            min={fieldDefinition.displayMin ?? fieldDefinition.min ?? 0}
            max={resolvedDisplayMax}
            labels={fieldDefinition.labels}
            otherInitialValue={fieldDefinition.otherInitialValue}
            value={rhfField.value ?? null}
            onChange={v => rhfField.onChange(v)}
            disabled={fieldDefinition.disabled}
            hideOther={resolvedMax !== undefined && resolvedDisplayMax >= resolvedMax}
          />
        );
      }
    }

    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        value={autoCalculatedValue ?? rhfField.value ?? ''}
        onChange={
          isAutoCalculated
            ? undefined
            : e => rhfField.onChange(e.target.value === '' ? null : Number(e.target.value))
        }
        readOnly={isAutoCalculated}
        min={fieldDefinition.min}
        max={fieldDefinition.max}
        type="number"
        className={cn(
          'noscroll',
          !fieldDefinition.stepper &&
            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
        )}
        onWheel={e => e.currentTarget.blur()}
        placeholder={placeholder}
        disabled={fieldDefinition.disabled}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'select') {
    const options =
      typeof fieldDefinition.options[0] === 'string'
        ? (fieldDefinition.options as string[]).map(value => ({
            value,
            label: t([
              `fields.${actualFieldName}.options.${value}`,
              `products.${value}.label`,
              `options.${value}`,
              value,
            ]),
          }))
        : (fieldDefinition.options as Array<{ label: string; value: string }>);

    if (fieldDefinition.icons) {
      return (
        <SelectCards
          options={options.map(opt => ({
            ...opt,
            icon: fieldDefinition.icons?.[opt.value],
          }))}
          value={rhfField.value || ''}
          onChange={v => rhfField.onChange(v)}
          disabled={fieldDefinition.disabled}
          name={fieldDefinition.name}
          perRow={fieldDefinition.perRow}
          maxPerRow={fieldDefinition.maxPerRow}
          display={fieldDefinition.display as 'cards' | 'small-cards' | 'badge' | undefined}
        />
      );
    }

    if (fieldDefinition.searchable) {
      return (
        <SearchableSelect
          options={options}
          value={rhfField.value || ''}
          onChange={v => rhfField.onChange(v)}
          clearable={fieldDefinition.clearable}
          searchable={fieldDefinition.searchable}
        />
      );
    }

    return (
      <Select
        onValueChange={v => rhfField.onChange(v === '---' ? '' : v)}
        value={rhfField.value || ''}
      >
        <SelectTrigger name={fieldDefinition.name} disabled={fieldDefinition.disabled}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent portalTarget={portalTarget}>
          {fieldDefinition.clearable && <SelectItem value="---">--- Aucune ---</SelectItem>}
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (fieldDefinition.type === 'multiSelect') {
    const options =
      fieldDefinition.options?.length && typeof fieldDefinition.options[0] === 'string'
        ? (fieldDefinition.options as string[]).map(value => ({
            value,
            label: String(
              t([
                `fields.${actualFieldName}.options.${value}`,
                `products.${value}.label`,
                `options.${value}`,
                value,
              ]),
            ),
          }))
        : ((fieldDefinition.options as Array<{ label: string; value: string }>) ?? []);

    // Convert values to Option objects for react-select
    // Values can be strings (from API) or Option objects (from previous selections)
    // Only include values that have a matching option (filters out corrupted data)
    const selectedOptions = (rhfField.value || [])
      .map((v: unknown) => {
        // Extract the value string from either format
        const valueStr = typeof v === 'string' ? v : (v as { value?: unknown })?.value;
        if (!valueStr || typeof valueStr !== 'string') return null;
        // Only return values that have a matching option
        return options.find(opt => opt.value === valueStr) ?? null;
      })
      .filter((v): v is { value: string; label: string } => v !== null);

    return (
      <MultiSelect
        options={options}
        onChange={v => {
          const values = v ? v.map(opt => (typeof opt.value === 'string' ? opt.value : '')) : [];
          rhfField.onChange(values.filter(Boolean));
        }}
        value={selectedOptions}
        placeholder={placeholder}
        isDisabled={fieldDefinition.disabled}
        portalTarget={portalTarget}
      />
    );
  }

  if (fieldDefinition.type === 'company') {
    return (
      <CompanySearchInput
        {...rhfField}
        displayCompanyInformation={fieldDefinition.displayCompanyInformation}
        onCompanySelect={v => rhfField.onChange(v)}
      />
    );
  }

  if (fieldDefinition.type === 'radio') {
    const resolvedOptions = fieldDefinition.options.map(option =>
      typeof option === 'string'
        ? {
            value: option,
            label: t([
              `fields.${actualFieldName}.options.${option}`,
              `products.${option}.label`,
              `options.${option}`,
              option,
            ]),
          }
        : option,
    );

    if (fieldDefinition.icons) {
      return (
        <SelectCards
          options={resolvedOptions.map(opt => ({
            ...opt,
            icon: fieldDefinition.icons?.[opt.value],
          }))}
          value={rhfField.value?.toString() || ''}
          onChange={v => rhfField.onChange(v)}
          disabled={fieldDefinition.disabled}
          name={fieldDefinition.name}
          perRow={fieldDefinition.perRow}
          maxPerRow={fieldDefinition.maxPerRow}
          display={fieldDefinition.display as 'cards' | 'small-cards' | 'badge' | undefined}
        />
      );
    }

    if (fieldDefinition.display === 'toggle') {
      return (
        <RadioToggle
          options={resolvedOptions}
          value={rhfField.value?.toString()}
          onChange={v => rhfField.onChange(v)}
          disabled={fieldDefinition.disabled}
          name={fieldDefinition.name}
        />
      );
    }

    return (
      <RadioGroup
        onValueChange={rhfField.onChange}
        value={rhfField.value?.toString()}
        className="flex flex-col gap-2"
      >
        {resolvedOptions.map(({ label, value }) => (
          <Group key={value} gap={2}>
            <RadioGroupItem value={value} id={`${fieldDefinition.name}-${value}`} />
            <Label htmlFor={`${fieldDefinition.name}-${value}`}>{label}</Label>
          </Group>
        ))}
      </RadioGroup>
    );
  }

  if (fieldDefinition.type === 'radioBlock') {
    return (
      <RadioGroup value={rhfField.value?.toString()} onValueChange={rhfField.onChange}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {fieldDefinition.options.map(option => {
            const { label, value } =
              typeof option === 'string'
                ? {
                    value: option,
                    label: t([
                      `fields.${actualFieldName}.options.${option}`,
                      `products.${option}.label`,
                      `options.${option}`,
                      option,
                    ]),
                  }
                : option;

            const radioId = `${fieldDefinition.name}-${value}`;
            const isSelected = rhfField.value === value;

            return (
              <Label
                key={value}
                htmlFor={radioId}
                className={cn(
                  isSelected && 'border-primary bg-primary/5',
                  !isSelected && 'border-gray-200 hover:border-gray-300',
                  'flex items-center space-x-3 p-4 border-2 rounded-lg hover:shadow-sm transition-colors cursor-pointer',
                )}
              >
                <RadioGroupItem value={value.toString()} id={radioId} />
                <Text inline size="sm" weight="medium" className="flex-1">
                  {label}
                </Text>
              </Label>
            );
          })}
        </div>
      </RadioGroup>
    );
  }

  if (fieldDefinition.type === 'date') {
    // Native <input type="date"> only binds to YYYY-MM-DD. AI-extracted values
    // can arrive in other shapes (DD/MM/YYYY, ISO with time, etc.) — coerce so
    // the input displays the date instead of falling back to its placeholder.
    const isoValue = toIsoDate(rhfField.value);
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        type="date"
        className="[appearance:textfield] noscroll"
        disabled={fieldDefinition.disabled}
        value={isoValue}
        min={fieldDefinition.min ?? '1900-01-01'}
        max={fieldDefinition.max ?? '9999-12-31'}
        clearable={fieldDefinition.clearable}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'month') {
    return (
      // Input type="month" has poor browser support (no Firefox support, nor Safari support)
      <FormMonthInput
        {...rhfField}
        field={fieldDefinition}
        value={rhfField.value || ''}
        min={fieldDefinition.min ?? '1900-01'}
        max={fieldDefinition.max ?? '9999-12'}
      />
    );
  }

  if (fieldDefinition.type === 'datetime' && contractTimeZone) {
    return (
      <DateTimeInput
        name={rhfField.name}
        value={rhfField.value || undefined}
        fromDate={parseDateLimit(fieldDefinition.min)?.toDate()}
        toDate={parseDateLimit(fieldDefinition.max)?.toDate()}
        disabled={fieldDefinition.disabled}
        onChange={value => {
          if (value) {
            rhfField.onChange(value);
          }
        }}
      />
    );
  }

  if (fieldDefinition.type === 'datetime') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <Input
        {...rhfField}
        type="datetime-local"
        className="[appearance:textfield] noscroll"
        disabled={fieldDefinition.disabled}
        value={rhfField.value || ''}
        min={resolveDateTimeToken(fieldDefinition.min) ?? '1900-01-01T00:00'}
        max={resolveDateTimeToken(fieldDefinition.max) ?? '9999-12-31T23:59'}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
        onChange={e => {
          const value = e.target.value;

          if (value) {
            rhfField.onChange(value);
          }
        }}
      />
    );
  }

  if (fieldDefinition.type === 'textWithTransform') {
    return (
      <Input
        {...rhfField}
        disabled={fieldDefinition.disabled}
        value={fieldDefinition.transformMapping?.[rhfField.value] ?? rhfField.value ?? ''}
      />
    );
  }

  if (fieldDefinition.type === 'checkbox') {
    // AssetFunnelModule and CustomerFunnelModule format field names like so: {arrayName}.${index}.${actualFieldName}
    const actualFieldName =
      fieldDefinition.name.startsWith('assets.') || fieldDefinition.name.startsWith('customers.')
        ? fieldDefinition.name.split('.').slice(2).join('.')
        : fieldDefinition.name;

    return (
      <Group justify="start" gap={2}>
        <Checkbox
          {...rhfField}
          id={fieldDefinition.name}
          checked={Boolean(rhfField.value)}
          onCheckedChange={v => rhfField.onChange(v)}
        />
        <FormLabel className="flex" htmlFor={fieldDefinition.name}>
          {fieldDefinition.label ??
            t([`fields.${actualFieldName}.form.label`, `fields.${actualFieldName}.label`])}
        </FormLabel>
      </Group>
    );
  }

  if (fieldDefinition.type === 'vehicleVersion') {
    return <VehicleVersionFormField watchedFieldNames={fieldDefinition.watchedFieldNames} />;
  }

  if (fieldDefinition.type === 'vehicleRegistration') {
    const FieldIcon = fieldDefinition.icon;
    return (
      <VehicleRegistrationNumberInput
        placeholder={placeholder}
        icon={FieldIcon ? <FieldIcon className="h-4 w-4" /> : undefined}
      />
    );
  }

  if (fieldDefinition.type === 'vehicleInput') {
    return <VehicleInputFormField field={fieldDefinition} />;
  }

  if (fieldDefinition.type === 'vehicleBrand') {
    return <VehicleBrandFormField field={fieldDefinition} />;
  }

  if (fieldDefinition.type === 'vehicleModel') {
    return (
      <VehicleModelFormField
        field={fieldDefinition}
        watchedFieldNames={fieldDefinition.watchedFieldNames}
      />
    );
  }

  if (fieldDefinition.type === 'hiscoxActivity') {
    return <HiscoxActivityFormField field={fieldDefinition} />;
  }

  if (fieldDefinition.type === 'array') {
    return (
      <ArrayFormField
        fieldDefinition={fieldDefinition}
        portalTarget={portalTarget}
        externalData={externalData}
      />
    );
  }

  if (fieldDefinition.type === 'hiscoxMrpExcess') {
    return (
      <HiscoxMrpExcessFormField
        field={fieldDefinition}
        rhfField={rhfField}
        externalData={externalData}
        portalTarget={portalTarget}
      />
    );
  }

  return null;
}

export function FormBuilderFields({
  fields,
  isLoading,
  externalData,
  portalTarget,
  className,
  sectionDisplay,
}: {
  fields: Fields;
  isLoading?: boolean;
  externalData?: Record<string, unknown>;
  portalTarget?: HTMLElement | null;
  className?: string;
  sectionDisplay?: 'column' | 'row';
}) {
  const form = useFormContext();

  const hasColSpan = fields.some(f => f.colSpan !== undefined);

  const gridClass = hasColSpan
    ? 'lg:grid lg:grid-cols-12 lg:gap-4'
    : sectionDisplay === 'row' && fields.length > 1
      ? cn(
          'lg:grid lg:gap-4',
          // Tailwind needs to be explicit about the classnames, it cannot be dynamic
          // https://tailwindcss.com/docs/detecting-classes-in-source-files
          fields.length === 2 && 'lg:grid-cols-2',
          fields.length >= 3 && 'lg:grid-cols-3',
        )
      : undefined;

  return (
    <AddressSearchSessionProvider>
      <Stack className={cn('gap-4', gridClass, className)}>
        {fields.map(f => {
          if (f.hidden) {
            return null;
          }

          const colSpanClass = hasColSpan ? LG_COL_SPAN_CLASSES[f.colSpan ?? 12] : undefined;

          return (
            <ConditionalFieldWrapper
              key={f.name}
              field={f}
              externalData={externalData}
              className={colSpanClass}
            >
              <FormField
                control={form.control}
                name={f.name}
                render={({ field }) => (
                  <FormBuilderField field={f} isLoading={isLoading}>
                    <FormBuilderInputRenderer
                      fieldDefinition={f}
                      rhfField={field}
                      portalTarget={portalTarget}
                      externalData={externalData}
                    />
                  </FormBuilderField>
                )}
              />
            </ConditionalFieldWrapper>
          );
        })}
      </Stack>
    </AddressSearchSessionProvider>
  );
}

export function FormBuilder<TFormValues extends FieldValues>({
  fields,
  form,
  className,
  children,
  isLoading,
  externalData,
  portalTarget,
}: FormBuilderProps<TFormValues>) {
  return (
    <Form {...form}>
      <form className={cn(className, 'flex flex-col gap-4')} onSubmit={e => e.preventDefault()}>
        <FormBuilderFields
          fields={fields}
          isLoading={isLoading}
          externalData={externalData}
          portalTarget={portalTarget}
        />
        {children}
      </form>
    </Form>
  );
}

export function SectionAutoFillButton({ fields }: { fields: Fields }) {
  const { t } = useTranslation();
  const form = useFormContext();
  const handleAutoFill = () => {
    const fieldNames = fields.map(field => field.name as string);
    autoFillFromPlaceholders(fieldNames, form.setValue, fields);
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="shrink-0 w-fit ml-auto"
      onClick={handleAutoFill}
    >
      <Wand2 className="mr-2 h-4 w-4" />
      {t('autoFill.button')}
    </Button>
  );
}

export function FormBuilderSections({
  sections,
  isLoading,
  externalData,
  portalTarget,
  disabled,
}: {
  sections: FormSections;
  isLoading?: boolean;
  externalData?: Record<string, unknown>;
  portalTarget?: HTMLElement | null;
  disabled?: boolean;
}) {
  const showAutoFill = shouldShowAutoFill();

  return (
    <Stack>
      {sections.map((section, index) => {
        const title = section.title;
        const description = section.description;
        return (
          <ConditionalSectionWrapper
            key={title || index}
            section={section}
            externalData={externalData}
          >
            <Card>
              {(title || description || showAutoFill) && (
                <CardHeader>
                  <Group justify="between" gap={0}>
                    <div>
                      {title && <CardTitle>{title}</CardTitle>}
                      {description && (
                        <Text size="sm" tone="muted">
                          {description}
                        </Text>
                      )}
                    </div>
                    {showAutoFill && <SectionAutoFillButton fields={section.fields} />}
                  </Group>
                </CardHeader>
              )}
              <CardContent>
                <FormBuilderFields
                  fields={section.fields.map(field => ({
                    ...field,
                    disabled: field.disabled || disabled,
                  }))}
                  isLoading={isLoading}
                  externalData={externalData}
                  portalTarget={portalTarget}
                  sectionDisplay={section.display}
                />
              </CardContent>
              {section.submitButton && (
                <CardFooter>
                  <Group align="stretch" className="w-full">
                    <div className="ml-auto">
                      <span>{section.submitButton}</span>
                    </div>
                  </Group>
                </CardFooter>
              )}
            </Card>
          </ConditionalSectionWrapper>
        );
      })}
    </Stack>
  );
}

export function SectionedFormBuilder<TFormValues extends FieldValues>({
  sections,
  form,
  className,
  children,
  isLoading,
  externalData,
  portalTarget,
  disabled,
}: SectionedFormBuilderProps<TFormValues>) {
  return (
    <Form {...form}>
      <form className={cn(className, 'flex flex-col gap-4')} onSubmit={e => e.preventDefault()}>
        <FormBuilderSections
          sections={sections}
          isLoading={isLoading}
          externalData={externalData}
          portalTarget={portalTarget}
          disabled={disabled}
        />
        <ComputedFieldsFormErrors fields={sections.flatMap(section => section.fields)} />
        {children}
      </form>
    </Form>
  );
}
